// Aussiebrand Studio products: the product page gallery and the collection cards.
// The section a shopper browses (men's or women's) is remembered for the visit, so a product page opened from it leads
// with that section's models, even after Dawn re-renders the gallery for another color.

const studioGender = {
  get() { try { return sessionStorage.getItem('studio-gender') || ''; } catch (e) { return ''; } },
  set(g) { try { if (g) sessionStorage.setItem('studio-gender', g); } catch (e) {} },
};
// The model a card showed, per product and color ("men:3"), so its product page leads with the same model.
const studioModel = {
  key: (handle, colour) => `studio-model:${handle}|${colour}`,
  get(handle, colour) { try { return sessionStorage.getItem(this.key(handle, colour)) || ''; } catch (e) { return ''; } },
  set(handle, colour, value) { try { sessionStorage.setItem(this.key(handle, colour), value); } catch (e) {} },
};

// Favorites: product handles saved in this browser (no account needed). Hearts anywhere on the page stay in step, and
// the header heart shows the count.
const aussieFavorites = {
  key: 'aussie-favorites',
  all() { try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; } },
  has(h) { return this.all().includes(h); },
  toggle(h) {
    const list = this.all(), next = list.includes(h) ? list.filter(x => x !== h) : [...list, h];
    try { localStorage.setItem(this.key, JSON.stringify(next)); } catch (e) {}
    this.paint();
    document.dispatchEvent(new CustomEvent('aussie:favorites', { detail: next }));
  },
  paint() {
    const list = this.all();
    document.querySelectorAll('.aussie-fav').forEach(b => {
      const on = list.includes(b.dataset.fav);
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on));
    });
    document.querySelectorAll('.aussie-fav-count').forEach(c => {
      if (c.textContent !== String(list.length)) c.textContent = list.length;   // only on change: avoids observer loops
      c.hidden = !list.length;
    });
  }
};
// Product lists are in random order, hats and shirts evenly mixed (the owner's rules): each <ul data-shuffle> is
// shuffled once (a collection grid only when the shopper hasn't picked a sort; the grid is re-rendered, and so
// re-shuffled, when filters change). Hats and the rest are each shuffled, then woven together so the smaller group is
// spread evenly through the list. With data-limit, the first N are shown and the rest hidden.
function studioShuffle(a) {
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}
function studioShuffleGrids() {
  document.querySelectorAll('ul[data-shuffle]:not([data-shuffled])').forEach((list) => {
    const isHat = (li) => !!li.querySelector('.studio-card--hat');
    const groups = [studioShuffle([...list.children].filter(isHat)), studioShuffle([...list.children].filter((li) => !isHat(li)))];
    // Each next spot goes to the group furthest behind its share of the list, so both are spread evenly.
    const total = list.children.length, used = [0, 0], items = [];
    for (let n = 0; n < total; n++) {
      const g = groups.map((x, i) => (used[i] < x.length ? used[i] / x.length : Infinity)).reduce((best, v, i, all) => (v < all[best] ? i : best), 0);
      items.push(groups[g][used[g]++]);
    }
    const limit = parseInt(list.dataset.limit, 10);
    items.forEach((li, i) => { list.appendChild(li); if (limit) li.hidden = i >= limit; });
    list.dataset.shuffled = '';
  });
}
// "More to explore" never shows the product being viewed, and shows at most 4.
function aussieTidyRelated() {
  const list = document.querySelector('.related-products ul');
  if (!list) return;
  const here = location.pathname.split('/products/')[1];
  [...list.children].forEach(li => {
    const a = li.querySelector('.studio-card__link');
    if (here && a && a.pathname.split('/products/')[1] === here) li.remove();
  });
  [...list.children].slice(4).forEach(li => li.remove());
}
let aussiePaintQueued = false;
const aussieRepaint = () => {
  if (aussiePaintQueued) return;
  aussiePaintQueued = true;
  requestAnimationFrame(() => {
    aussiePaintQueued = false; aussieFavorites.paint();
    studioShuffleGrids();   // before trimming "More to explore", so its 4 are a random pick
    aussieTidyRelated();
    if (typeof studioSpreadModels === 'function') studioSpreadModels();   // cards added later (recommendations, filters)
  });
};
document.addEventListener('click', e => {
  const b = e.target.closest('.aussie-fav');
  if (!b) return;
  e.preventDefault(); e.stopPropagation();
  aussieFavorites.toggle(b.dataset.fav);
});
// Cards and galleries are re-rendered (color changes, recommendations): repaint hearts as they appear.
new MutationObserver(aussieRepaint).observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener('DOMContentLoaded', aussieRepaint);

// One large preview box; the thumbnails (this color's photos) switch it.
if (!customElements.get('studio-gallery')) {
  customElements.define('studio-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.gender) studioGender.set(this.dataset.gender);
      const gender = this.dataset.gender || studioGender.get();
      const thumbs = [...this.querySelectorAll('.studio-gallery__thumb')];
      const views = ['front', 'back', 'sleeve', 'other'];
      // Each color shows ONE model per section, spread across the checked models (data-model-pick, set per color and
      // product, so a color always gets the same model, also on its collection card).
      const pick = parseInt(this.dataset.modelPick, 10) || 0;
      // The model the shopper saw on the collection card for this color, when there was one.
      const [cardGender, cardModel] = studioModel.get(this.dataset.handle, this.dataset.colour).split(':');
      const chosen = g => {
        const ids = [...new Set(thumbs.filter(t => t.dataset.kind === g).map(t => t.dataset.model))];
        if (g === cardGender && ids.includes(cardModel)) return cardModel;
        return ids.length ? ids[pick % ids.length] : null;
      };
      const keep = { men: chosen('men'), women: chosen('women') };
      // In a men's or women's section, only that section's model; with no section, the chosen man and woman.
      const shown = t => t.dataset.kind === 'plain' || (t.dataset.model === keep[t.dataset.kind] && (!gender || t.dataset.kind === gender));
      // Order (the user's rule): all model shots first (front, back, sleeve), then the product shots.
      const rank = (t, i) => {
        const v = views.indexOf(t.dataset.view), k = t.dataset.kind;
        if (k === 'plain') return 1000 + v * 10 + i / 1000;
        const first = gender ? k === gender : k === 'men';
        return (first ? 0 : 100) + v * 10 + i / 1000;
      };
      const ordered = thumbs.map((t, i) => [rank(t, i), t]).sort((a, b) => a[0] - b[0]).map(([, t]) => t);
      ordered.forEach(t => t.parentNode.appendChild(t));
      thumbs.forEach(t => { t.hidden = !shown(t); });
      const main = this.querySelector('.studio-gallery__image');
      const show = t => {
        if (!main || !t) return;
        main.src = t.dataset.src; main.removeAttribute('srcset'); main.alt = t.getAttribute('aria-label');
        main.parentElement.classList.toggle('is-product', t.dataset.kind === 'plain');   // product-only shots shown whole
        thumbs.forEach(x => x.classList.toggle('is-active', x === t));
      };
      thumbs.forEach(t => { t.addEventListener('click', () => show(t)); t.addEventListener('mouseenter', () => show(t)); });
      show(ordered.find(t => !t.hidden));
      // Hover zoom (desktop only, no click): the photo magnifies about 2x in place, following the pointer.
      const box = this.querySelector('.studio-gallery__main');
      if (box && main && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        box.addEventListener('mousemove', e => {
          const r = box.getBoundingClientRect();
          main.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          box.classList.add('is-zoomed');
        });
        box.addEventListener('mouseleave', () => box.classList.remove('is-zoomed'));
      }
    }
  });
}

// Collection card: swatches switch the color; the other view shows on mouse rollover (CSS; not on hats). Shirts with
// a big back design lead with the back (data-lead="back").
if (!customElements.get('studio-card')) {
  customElements.define('studio-card', class extends HTMLElement {
    connectedCallback() {
      const front = this.querySelector('.studio-card__front'), back = this.querySelector('.studio-card__back');
      const link = this.querySelector('.studio-card__link');
      const label = this.querySelector('.studio-card__colour'), swatches = [...this.querySelectorAll('.studio-card__swatch')];
      const title = this.querySelector('.studio-card__title');
      this.swatches = swatches;
      // Shows swatch s with model id (one of its data-models), or the photos worked out in Liquid without one.
      this.pick = (s, id = s && s.dataset.modelId) => {
        if (!s) return;
        const m = studioModels(s)[id];
        let lead = s.dataset.front, roll = s.dataset.back, isModel = !!s.dataset.model;
        if (m) {
          const plainBack = s.dataset.plainBack || '';
          if (this.dataset.lead === 'back' && (m.b || plainBack)) { lead = m.b || plainBack; roll = m.f || ''; isModel = !!m.b; }
          else if (m.f) { lead = m.f; roll = m.b || plainBack; isModel = true; }
        }
        this.swatch = s; this.modelId = m ? id : '';
        front.src = lead;
        // Model photos fill the card; product-only photos (hats, plain shirts) are shown whole, with room around them.
        front.closest('.studio-card__media').classList.toggle('studio-card__media--product', !isModel);
        if (back) { if (roll && roll !== lead) { back.src = roll; back.hidden = false; } else back.hidden = true; }
        if (label) label.textContent = s.dataset.colour;
        if (s.dataset.url) link.href = s.dataset.url;   // the product page opens on this color
        if (s.dataset.title && title) title.textContent = s.dataset.title;   // hats: each color is its own product
        swatches.forEach(x => x.classList.toggle('is-active', x === s));
      };
      swatches.forEach(s => s.addEventListener('click', e => { e.preventDefault(); this.pick(s); studioSpreadModels(); }));
      this.pick(swatches.find(s => s.classList.contains('is-active')) || swatches[0]);
      // Opening a product from a men's or women's section keeps that section's models (and this card's model) on its page.
      link.addEventListener('click', () => {
        studioGender.set(this.dataset.gender);
        if (this.modelId && this.swatch) studioModel.set(this.dataset.handle, this.swatch.dataset.colour, `${this.dataset.gender}:${this.modelId}`);
      });
    }
  });
}

// A swatch's models: {id: {f: front url, b: back url}}, from data-models ("<id>~f|b~<url>|...").
function studioModels(s) {
  const out = {};
  (s.dataset.models || '').split('|').forEach(part => {
    const [id, view, url] = part.split('~');
    if (!id || !url) return;
    (out[id] = out[id] || {})[view] = url;
  });
  return out;
}

// No model next to itself: in each grid, no card shows the same model as the card to its left or the one above it.
// Each card tries its color's models starting from its usual one (so the choice is stable), backing up to earlier
// cards when it runs out. Re-run when the layout changes (columns differ between phone and desktop).
function studioSpreadModels() {
  const groups = new Map();
  document.querySelectorAll('studio-card').forEach(c => {
    if (!c.pick || !c.swatch || c.closest('[hidden]')) return;   // hidden cards (past a list's limit) have no neighbors
    const g = c.closest('ul') || c.parentElement;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(c);
  });
  groups.forEach(cards => {
    const items = cards.map((c, i) => {
      const r = c.getBoundingClientRect(), s = c.swatch, ids = Object.keys(studioModels(s));
      const start = Math.max(0, ids.indexOf(s.dataset.modelId));
      return { c, top: r.top, left: r.left, i, options: ids.length ? ids.slice(start).concat(ids.slice(0, start)) : [''] };
    });
    // The cards to the left and above each card.
    items.forEach((it, i) => {
      const prev = items[i - 1];
      it.near = [prev && Math.abs(prev.top - it.top) < 2 ? prev : null,
        items.slice(0, i).reverse().find(p => Math.abs(p.left - it.left) < 2)].filter(Boolean);
    });
    const key = (it, id) => (id ? `${it.c.dataset.gender}:${id}` : '');
    const choice = [];
    let steps = 0;
    const place = i => {
      if (i === items.length) return true;
      const it = items[i];
      for (const id of it.options) {
        if (++steps > 5000) return false;
        const k = key(it, id);
        if (k && it.near.some(n => key(n, choice[n.i]) === k)) continue;
        choice[i] = id;
        if (place(i + 1)) return true;
      }
      return false;
    };
    // With no clash-free layout (too few models), go card by card, avoiding what can be avoided.
    if (!place(0)) items.forEach((it, i) => {
      choice[i] = it.options.find(id => !it.near.some(n => key(n, choice[n.i]) === key(it, id))) ?? it.options[0];
    });
    items.forEach((it, i) => { if (choice[i] && choice[i] !== it.c.modelId) it.c.pick(it.c.swatch, choice[i]); });
  });
}
document.addEventListener('DOMContentLoaded', () => { studioShuffleGrids(); studioSpreadModels(); });
let studioSpreadTimer;
window.addEventListener('resize', () => { clearTimeout(studioSpreadTimer); studioSpreadTimer = setTimeout(studioSpreadModels, 200); });

// Product details rows: one open at a time; opening one closes the others (the owner's rule, RULES 6).
// (toggle doesn't bubble, so it's caught on the way down.)
document.addEventListener('toggle', (e) => {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open || !d.closest('.product__accordion')) return;
  const box = d.closest('.product__info-container') || document;
  box.querySelectorAll('.product__accordion details[open]').forEach((other) => { if (other !== d) other.open = false; });
}, true);

// Breadcrumb: clicking the collection the shopper just came from goes back, so its filters and scroll are kept.
document.addEventListener('click', (e) => {
  const a = e.target.closest('.aussie-crumbs a[data-crumb-back]');
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey || !document.referrer) return;
  try {
    const from = new URL(document.referrer), to = new URL(a.href, location.href);
    if (from.origin === location.origin && from.pathname === to.pathname && history.length > 1) {
      e.preventDefault();
      history.back();
    }
  } catch (err) {}
});
